import { getRouteApi } from '@tanstack/react-router';
import { CalendarOff, ClipboardList, Package, Truck } from 'lucide-react';
import { useId, useMemo, useState, type FormEvent } from 'react';

import { BarraFiltros, type ChipFiltro } from '@/componentes/BarraFiltros';
import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { Campo, CampoSelecao } from '@/componentes/Campo';
import { EtiquetaDoPreenchido, LinhaDoPreenchido } from '@/componentes/LinhaDoPreenchido';
import { ListaDeEscolha } from '@/componentes/ListaDeEscolha';
import { CardIndicador } from '@/componentes/CardIndicador';
import { ControleCarregando } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { JanelaDeAcao } from '@/componentes/JanelaDeAcao';
import { Medida } from '@/componentes/Medida';
import { RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { HistoriaNaMesa } from '@/historias/mesa';
import { formatarDataHora, formatarDecimal, formatarInteiro } from '@/lib/formato';
import { opcaoDaMaquina } from '@/modulos/equipamentos/opcao-da-maquina';
import { useOpcoesComVisualDaFrota } from '@/modulos/equipamentos/opcoesComVisual';
import { useSessao } from '@/modulos/sessao/consultas';

import type { OrdemDaApi, TipoDeOrdem } from './api';
import { ordensAbertasDaMaquina, PRIORIDADE_PADRAO, prioridadeDaParada } from './autopreenchimento';
import {
  FILTRO_PADRAO,
  OPCOES_POR_PAGINA,
  PADRAO_POR_PAGINA,
  ehFiltroDaOrdem as ehFiltro,
  montarBuscaDasOrdens,
  type Filtro,
} from './busca';
import { explicarFalha, useAbrirOrdem, useCadastrosDaOficina, useOrdens } from './consultas';
import {
  contarMaquinasNaOficina,
  contarOficina,
  estaFechada,
  numeroDaOrdem,
  ROTULO_DA_ETAPA,
  ROTULO_DA_PRIORIDADE,
  ROTULO_DO_TIPO,
} from './ordem';
import { PainelDaOrdem, SeloDePrioridade } from './PainelDaOrdem';
import { janelaCurta, linhaCurtaDaParada, momentoDaParada } from './parada';

/**
 * Oficina e ordens de serviço — módulo 06 do mapa funcional v6; passo 6 da
 * sequência do ciclo 1.
 *
 * ## A fila, e a pergunta que ela responde
 *
 * O v6 escreve o risco do módulo por extenso: "máquinas esquecidas na fila". A
 * fila abre pelo que importa — prioridade, depois o que está aberto há mais
 * tempo — e cada linha diz em que etapa a O.S. está e DESDE QUANDO, porque
 * "aguardando peça há 9 dias" é outra conversa que "aguardando peça".
 *
 * ## O que ela ainda não faz, e diz
 *
 * A peça prevista com produto e depósito vira reserva, e aplicada vira saída
 * do livro do estoque, pelo banco (0020); foto e anexo entram enquanto a O.S.
 * está aberta, e não se editam nem se apagam (0026). A hipótese da IA tem
 * lugar reservado e nasce vazia até alguém pedi-la.
 */

const ROTULO_DO_FILTRO: Readonly<Record<Filtro, string>> = {
  abertas: 'Abertas',
  fechadas: 'Encerradas e canceladas',
  /* 0101: a corretiva que fechou sem diagnóstico e causa raiz, de antes da
     regra. A liberada se completa no encerramento; a encerrada fica à vista. */
  sem_causa: 'Sem causa registrada',
  todas: 'Todas',
};

function noFiltro(o: OrdemDaApi, filtro: Filtro): boolean {
  if (filtro === 'todas') return true;
  if (filtro === 'sem_causa') return o.semCausa === true;
  return filtro === 'abertas' ? !estaFechada(o.etapa) : estaFechada(o.etapa);
}

const rota = getRouteApi('/ordens');

export function PaginaOrdensDeServico() {
  const sessao = useSessao();
  const permissoes = sessao.data?.permissoes ?? [];
  const pode = (codigo: string) => permissoes.includes(codigo);

  const ordens = useOrdens();
  const [abrindo, setAbrindo] = useState(false);
  const [aberta, setAberta] = useState<string | null>(null);
  /* O FILTRO MORA NA URL (p.16): "olha as O.S. que fecharam no mes" e uma
     conversa que acontece por link, e com o filtro em `useState` o link abria
     sempre em "Abertas" — quem recebia achava que via o que foi mandado. */
  const busca = rota.useSearch();
  const navegar = rota.useNavigate();
  const filtro = busca.filtro ?? FILTRO_PADRAO;
  const pagina = busca.pagina ?? 1;
  const porPagina = busca.porPagina ?? PADRAO_POR_PAGINA;

  const todas = useMemo(() => ordens.data ?? [], [ordens.data]);
  const contagem = useMemo(() => contarOficina(todas), [todas]);
  const naOficina = useMemo(() => contarMaquinasNaOficina(todas), [todas]);
  const linhas = useMemo(() => todas.filter((o) => noFiltro(o, filtro)), [todas, filtro]);

  const paginas = Math.max(Math.ceil(linhas.length / porPagina), 1);
  const paginaSegura = Math.min(pagina, paginas);
  const inicio = (paginaSegura - 1) * porPagina;
  const visiveis = linhas.slice(inicio, inicio + porPagina);

  /* Trocar o filtro e NAVEGAR, nao mexer em estado: assim o botao de voltar
     do navegador desfaz a troca, que e o que a pessoa espera dele. */
  function irPara(proximo: {
    readonly filtro?: Filtro;
    readonly pagina?: number;
    readonly porPagina?: number;
  }) {
    void navegar({
      search: montarBuscaDasOrdens({
        filtro: proximo.filtro ?? filtro,
        pagina: proximo.pagina ?? 1,
        porPagina: proximo.porPagina ?? porPagina,
      }),
    });
  }

  function filtrar(novo: Filtro) {
    irPara({ filtro: novo, pagina: 1 });
  }

  /* "Abertas" é o ponto de partida da tela, mas é filtro: a lista está
     recortada, então o recorte aparece como chip e sai com o "✕" (p.16). */
  const chips: readonly ChipFiltro[] =
    filtro === 'todas'
      ? []
      : [
          {
            id: 'mostrar',
            rotulo: `Mostrar: ${ROTULO_DO_FILTRO[filtro]}`,
            aoRemover: () => {
              filtrar('todas');
            },
          },
        ];

  const COLUNAS: readonly Coluna<OrdemDaApi>[] = [
    {
      chave: 'numero',
      rotulo: 'O.S.',
      celula: (o) => (
        <span className="flex flex-col">
          {/* Código em monoespaçada, para comparar na vertical (p.18). */}
          <span className="codigo whitespace-nowrap text-tinta">{numeroDaOrdem(o.numero)}</span>
          <span className="txt-apoio whitespace-nowrap text-tinta-secundaria">{ROTULO_DO_TIPO[o.tipo]}</span>
        </span>
      ),
    },
    {
      chave: 'equipamento',
      rotulo: 'Equipamento',
      celula: (o) => (
        <span className="flex flex-col">
          <TextoDaCelula texto={`${o.equipamento} · ${o.equipamentoDescricao}`} classe="txt-corpo text-tinta" linhas={1} />
          {/* A PARADA PROGRAMADA (R06, p.70) é a segunda linha, em violeta,
              com o ícone dela; passada a volta combinada, o atraso em
              vermelho. Sem programação, o que a O.S. fez com a máquina — e
              não um status: os seis nomes oficiais só aparecem em selo. */}
          {o.paradaProgramada && !o.maquinaParada ? (
            <LinhaDaParada de={new Date(o.paradaProgramada.de)} ate={new Date(o.paradaProgramada.ate)} />
          ) : (
            <span className="txt-apoio whitespace-nowrap text-tinta-secundaria">
              {o.maquinaParada ? 'Parou por esta O.S.' : 'Não parou por esta O.S.'}
            </span>
          )}
        </span>
      ),
    },
    {
      chave: 'sintoma',
      rotulo: 'Sintoma',
      /* TEXTO LIVRE: até duas linhas, quebrando entre palavras, e com teto de
         largura — um sintoma de cem letras numa linha só empurrava Prioridade
         e Etapa para fora do quadro (p.18; R01, p.28). O texto inteiro fica no
         `title` e na aba Resumo do painel. */
      celula: (o) => (
        <span className="txt-corpo line-clamp-2 min-w-40 max-w-96 whitespace-normal text-pretty text-tinta" title={o.sintoma}>
          {o.sintoma}
        </span>
      ),
    },
    {
      chave: 'prioridade',
      rotulo: 'Prioridade',
      historia: 'SEL-OSV-02',
      alinhamento: 'status',
      celula: (o) => <SeloDePrioridade rotulo={ROTULO_DA_PRIORIDADE[o.prioridade] ?? String(o.prioridade)} />,
    },
    {
      chave: 'etapa',
      rotulo: 'Etapa',
      alinhamento: 'status',
      celula: (o) => (
        <span className="flex flex-col">
          <span className="txt-corpo text-tinta">{ROTULO_DA_ETAPA[o.etapa]}</span>
          {/* Data com hora não parte (p.12). */}
          <span className="txt-apoio whitespace-nowrap text-tinta-secundaria">
            {o.etapaDesde ? `desde ${formatarDataHora(new Date(o.etapaDesde))}` : '—'}
          </span>
        </span>
      ),
    },
    {
      chave: 'mao',
      rotulo: 'Mão de obra',
      alinhamento: 'numero',
      /* Sai abaixo de 1280 px (R01, p.28); continua na aba Consumo do painel. */
      prioridade: 'secundaria',
      celula: (o) => <Medida valor={formatarDecimal(o.horasDeMaoDeObra, 1)} unidade="h" />,
    },
  ];

  return (
    <>
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          <CabecalhoPagina
            sobretitulo="Oficina"
            titulo="Ordens de serviço"
            subtitulo="O que está em reparo, o que está esperando, e o que impede a liberação"
            acoes={
              pode('os.abrir') ? (
                /* Abrir O.S. tem cinco campos: MODAL ao centro (R01, p.29). */
                <HistoriaNaMesa codigo="BTN-OSV-01" className="flex">
                  <JanelaDeAcao
                    rotulo="Abrir O.S."
                    titulo="Abrir O.S."
                    nivel="primario"
                    aberta={abrindo}
                    aoAbrir={() => {
                      setAbrindo(true);
                    }}
                    aoFechar={() => {
                      setAbrindo(false);
                    }}
                  >
                    <FormularioDeAbertura
                      aoFechar={(id) => {
                        setAbrindo(false);
                        if (id) setAberta(id);
                      }}
                    />
                  </JanelaDeAcao>
                </HistoriaNaMesa>
              ) : null
            }
          />

          {/* CONTEXTO É A BASE, EM FRASE CURTA (p.17): "1 de 3 máquinas na
              oficina". A definição de cada um quebrava em três linhas no card
              de 1024 px; ela vai uma vez, na linha de ajuda abaixo da faixa. */}
          <FaixaIndicadores>
            {/* Carregando (p.32): o rótulo e o ícone ficam, e o número vira
                esqueleto — antes, "0" no lugar de uma fila que não tinha chegado. */}
            <CardIndicador
              rotulo="O.S. abertas"
              icone={ClipboardList}
              tom="manutencao"
              carregando={ordens.isPending}
              valor={formatarInteiro(contagem.abertas)}
              contexto="da solicitação ao teste"
            />
            <CardIndicador
              rotulo="Máquinas paradas"
              icone={Truck}
              tom="parada"
              carregando={ordens.isPending}
              /* R02: máquina parada é o próprio alerta. Zero sai em preto. */
              alerta={contagem.maquinasParadas > 0}
              valor={formatarInteiro(contagem.maquinasParadas)}
              /* Vazio enquanto a fila vem: o card desenha a barra (p.32). */
              contexto={ordens.isPending ? '' : `de ${formatarInteiro(naOficina)} ${naOficina === 1 ? 'máquina' : 'máquinas'} na oficina`}
            />
            <CardIndicador
              rotulo="Aguardando peça"
              icone={Package}
              tom="standby"
              carregando={ordens.isPending}
              valor={formatarInteiro(contagem.aguardandoPeca)}
              contexto={ordens.isPending ? '' : `de ${formatarInteiro(contagem.abertas)} O.S. ${contagem.abertas === 1 ? 'aberta' : 'abertas'}`}
            />
            <CardIndicador
              rotulo="Liberadas"
              icone={ClipboardList}
              tom="operando"
              carregando={ordens.isPending}
              valor={formatarInteiro(contagem.paraEncerrar)}
              contexto="falta encerrar a O.S."
            />
          </FaixaIndicadores>
          <p className="txt-apoio text-pretty text-tinta-secundaria">
            Máquina parada é a que a O.S. tirou da operação. Liberada é a O.S. que já devolveu a máquina: sai das
            abertas e espera o encerramento.
          </p>

          <BarraFiltros
            chips={chips}
            /* Enquanto a fila vem, a contagem vira barra (p.32): "0 de 0 O.S."
               afirmava uma fila vazia. */
            contagem={ordens.isPending ? null : `${formatarInteiro(linhas.length)} de ${formatarInteiro(todas.length)} O.S.`}
            aoLimpar={() => {
              filtrar('todas');
            }}
          >
            <CampoSelecao
              rotulo="Mostrar"
              valor={filtro}
              aplicado={filtro !== 'todas'}
              opcoes={(Object.keys(ROTULO_DO_FILTRO) as Filtro[]).map((f) => ({ valor: f, rotulo: ROTULO_DO_FILTRO[f] }))}
              aoMudar={(novo) => {
                filtrar(ehFiltro(novo) ? novo : 'todas');
              }}
            />
          </BarraFiltros>

          {/* A lista é a origem do painel: clicar em outra linha troca a O.S.
              aberta em vez de fechar o painel (p.19). */}
          <div data-painel-origem>
            <Tabela
              rotulo="Ordens de serviço"
              colunas={COLUNAS}
              linhas={visiveis}
              alturaDupla
              chaveDaLinha={(o) => o.id}
              carregando={ordens.isPending}
              selecionada={aberta}
              aoSelecionar={(o) => {
                setAberta(o.id);
              }}
              erro={
                ordens.isError ? (
                  <EstadoVazio
                    titulo="Não foi possível carregar as ordens de serviço"
                    descricao={explicarFalha(ordens.error).comoResolver}
                    acao={
                      <Botao
                        nivel="secundario"
                        onClick={() => {
                          void ordens.refetch();
                        }}
                      >
                        Carregar de novo
                      </Botao>
                    }
                  />
                ) : null
              }
              vazio={
                todas.length === 0 ? (
                  <EstadoVazio
                    titulo="Nenhuma ordem de serviço ainda"
                    descricao="Abra a primeira pela falha que chegou na oficina, ou pela preventiva vencida na tela de manutenção."
                  />
                ) : (
                  <EstadoVazio
                    titulo="Nenhuma O.S. neste filtro"
                    descricao="Troque o que mostrar para ver as outras."
                    acao={
                      <Botao
                        nivel="secundario"
                        onClick={() => {
                          filtrar('todas');
                        }}
                      >
                        Mostrar todas
                      </Botao>
                    }
                  />
                )
              }
              rodape={
                <RodapeTabela
                  primeiro={inicio + 1}
                  ultimo={inicio + visiveis.length}
                  total={linhas.length}
                  pagina={paginaSegura}
                  paginas={paginas}
                  porPagina={porPagina}
                  opcoesPorPagina={OPCOES_POR_PAGINA}
                  aoMudarPagina={(nova) => {
                    irPara({ pagina: nova });
                  }}
                  aoMudarPorPagina={(novo) => {
                    irPara({ porPagina: novo, pagina: 1 });
                  }}
                />
              }
            />
          </div>
        </div>
      </div>

      {/* O painel é IRMÃO da área rolável (R01, p.28): fixo ao lado da lista a
          partir de 1280 px, gaveta abaixo disso — e não depois da última linha. */}
      {aberta ? (
        <PainelDaOrdem
          key={aberta}
          id={aberta}
          pode={pode}
          aoFechar={() => {
            setAberta(null);
          }}
        />
      ) : null}
    </>
  );
}

/* ========================================================================
 * ABRIR O.S.
 * ===================================================================== */

type CampoDaAbertura = 'equipamento' | 'sintoma';

/**
 * ABRIR O.S. no desenho do lançamento de abastecimento (pedido do dono,
 * 31/10/2026): à vista só o que se digita a cada falha (a máquina, a
 * prioridade, o sintoma e se ela parou); o tipo, que quase sempre é
 * corretiva, e quem abre aparecem em etiqueta, e o tipo abre em "Ajustar
 * tipo". Marcar a máquina parada sobe a prioridade para "Máquina parada"
 * enquanto ninguém a escolheu à mão, e a máquina que já tem O.S. aberta é
 * avisada na ajuda do campo, antes da segunda O.S. para a mesma falha.
 */
function FormularioDeAbertura({ aoFechar }: { readonly aoFechar: (id: string | null) => void }) {
  const base = useId();
  const cadastros = useCadastrosDaOficina();
  const ordens = useOrdens();
  const sessao = useSessao();
  const comVisualDaFrota = useOpcoesComVisualDaFrota();
  const abrir = useAbrirOrdem();

  const [equipamentoId, setEquipamentoId] = useState('');
  const [tipo, setTipo] = useState<TipoDeOrdem>('corretiva');
  const [tipoAMao, setTipoAMao] = useState(false);
  const [prioridade, setPrioridade] = useState(String(PRIORIDADE_PADRAO));
  /* A prioridade escolhida pela pessoa não é mais mexida pela parada. */
  const [prioridadeAMao, setPrioridadeAMao] = useState(false);
  const [sintoma, setSintoma] = useState('');
  const [parada, setParada] = useState(false);
  const [ajustandoTipo, setAjustandoTipo] = useState(false);
  const [erros, setErros] = useState<Readonly<Partial<Record<CampoDaAbertura, string | null>>>>({});
  const [recusa, setRecusa] = useState<{ erro: string; comoResolver: string } | null>(null);

  const equipamentos = cadastros.data?.equipamentos ?? [];
  const codigoEscolhido = equipamentos.find((e) => e.id === equipamentoId)?.codigo ?? '';
  const jaAbertas = ordensAbertasDaMaquina(ordens.data ?? [], equipamentoId);
  const primeiraAberta = jaAbertas[0];

  function checar(campo: CampoDaAbertura): string | null {
    if (campo === 'equipamento') return equipamentoId === '' ? 'Escolha a máquina.' : null;
    return sintoma.trim() === '' ? 'Descreva o que foi observado na máquina.' : null;
  }

  /* Valida AO SAIR do campo (p.16), e limpa enquanto a pessoa corrige. */
  function sair(campo: CampoDaAbertura) {
    setErros((atual) => ({ ...atual, [campo]: checar(campo) }));
  }

  function marcarParada(marcada: boolean) {
    setParada(marcada);
    setPrioridade((atual) => String(prioridadeDaParada(marcada, Number(atual), prioridadeAMao)));
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const achados = { equipamento: checar('equipamento'), sintoma: checar('sintoma') };
    setErros(achados);
    if (achados.equipamento || achados.sintoma) return;
    setRecusa(null);
    abrir.mutate(
      { equipamentoId, tipo, prioridade: Number(prioridade), sintoma: sintoma.trim(), maquinaParada: parada },
      {
        onSuccess: (r) => {
          aoFechar(r.id);
        },
        onError: (falha) => {
          setRecusa(explicarFalha(falha));
        },
      },
    );
  }

  const prioridadeDoSistema = !prioridadeAMao && Number(prioridade) !== PRIORIDADE_PADRAO;

  return (
    <form onSubmit={enviar} noValidate className="flex flex-col gap-6">
      {/* A recusa do servidor no TOPO do formulário (p.22). */}
      {recusa === null ? null : (
        <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
          <span className="txt-corpo text-tinta">{recusa.erro}</span>
          <span className="txt-apoio text-tinta-secundaria">{recusa.comoResolver}</span>
        </div>
      )}

      <div className="formulario-grade">
        <Campo
          id={`${base}-eq`}
          rotulo="Equipamento"
          erro={erros.equipamento ?? null}
          {...(primeiraAberta
            ? {
                ajuda: `${codigoEscolhido} já tem ${jaAbertas.length > 1 ? `${formatarInteiro(jaAbertas.length)} O.S. abertas, entre elas a` : 'aberta a'} ${numeroDaOrdem(primeiraAberta.numero)} (${ROTULO_DA_ETAPA[primeiraAberta.etapa]}): ${primeiraAberta.sintoma}. Abra outra só se for outra falha.`,
              }
            : {})}
        >
          {/* As máquinas ainda vêm (p.32): a lista só com "Escolha a máquina"
              deixava escolher o nada. */}
          {cadastros.isPending ? (
            <ControleCarregando oQue="Carregando as máquinas" />
          ) : (
            /* O código interno e a placa em etiqueta (31/10/2026). */
            <ListaDeEscolha
              id={`${base}-eq`}
              rotulo="Equipamento"
              valor={equipamentoId}
              aoMudar={(valor) => {
                setEquipamentoId(valor);
                setErros((atual) => ({ ...atual, equipamento: null }));
              }}
              aoSair={() => {
                sair('equipamento');
              }}
              rotuloVazio="Escolha a máquina"
              opcoes={comVisualDaFrota(equipamentos.map((e) => opcaoDaMaquina(e)))}
              idDoErro={erros.equipamento ? `${base}-eq-erro` : undefined}
              adesivo
            />
          )}
        </Campo>
        <Campo
          id={`${base}-prioridade`}
          rotulo="Prioridade"
          preenchido={prioridadeDoSistema}
          {...(prioridadeDoSistema ? { ajuda: 'Subiu porque a máquina está parada. Troque se for outra.' } : {})}
        >
          <ListaDeEscolha
            id={`${base}-prioridade`}
            rotulo="Prioridade"
            valor={prioridade}
            aoMudar={(valor) => {
              setPrioridade(valor);
              setPrioridadeAMao(true);
            }}
            opcoes={[1, 2, 3, 4].map((p) => ({ valor: String(p), rotulo: ROTULO_DA_PRIORIDADE[p] ?? String(p) }))}
          />
        </Campo>

        {/* O sintoma é descrição: linha inteira (R01, largura pelo dado). */}
        <div className="campo-linha">
          <Campo id={`${base}-sintoma`} rotulo="Sintoma" erro={erros.sintoma ?? null} ajuda="O que foi observado, com as palavras de quem viu. O diagnóstico vem depois.">
            <input
              id={`${base}-sintoma`}
              type="text"
              autoComplete="off"
              className="controle w-full"
              placeholder="Vazamento de óleo no cilindro da lança"
              value={sintoma}
              onChange={(e) => {
                setSintoma(e.target.value);
                setErros((atual) => ({ ...atual, sintoma: null }));
              }}
              onBlur={() => {
                sair('sintoma');
              }}
              {...(erros.sintoma ? { 'aria-invalid': true, 'aria-describedby': `${base}-sintoma-erro` } : {})}
            />
          </Campo>
        </div>

        <label className="campo-linha flex items-center gap-2 txt-corpo text-tinta" htmlFor={`${base}-parada`}>
          <input
            id={`${base}-parada`}
            type="checkbox"
            checked={parada}
            onChange={(e) => {
              marcarParada(e.target.checked);
            }}
          />
          A máquina está parada por causa disto
        </label>

        {/* O que o sistema já respondeu, em etiqueta; o tipo abre no botão. A
            preventiva não é escolha daqui: ela se abre pela fila de
            manutenção, já com as tarefas do plano. */}
        <LinhaDoPreenchido
          rotulo="Tipo e quem abre"
          aberta={ajustandoTipo}
          aoAlternar={() => {
            setAjustandoTipo((aberto) => !aberto);
          }}
          rotuloDoBotao="tipo"
          idDoConteudo={`${base}-ajuste`}
        >
          <EtiquetaDoPreenchido rotulo="Tipo" valor={ROTULO_DO_TIPO[tipo]} marcada={!tipoAMao} />
          <EtiquetaDoPreenchido rotulo="Aberta por" valor={sessao.data?.nome ?? 'Quem está usando o sistema'} />
        </LinhaDoPreenchido>

        {ajustandoTipo ? (
          <div id={`${base}-ajuste`} className="campo-linha formulario-grade bloco-rebaixado p-4">
            <Campo
              id={`${base}-tipo`}
              rotulo="Tipo"
              preenchido={!tipoAMao}
              ajuda="A preventiva se abre pela fila de manutenção, já com as tarefas do plano."
            >
              <ListaDeEscolha
                id={`${base}-tipo`}
                rotulo="Tipo"
                valor={tipo}
                aoMudar={(valor) => {
                  setTipo(valor as TipoDeOrdem);
                  setTipoAMao(true);
                }}
                opcoes={(Object.keys(ROTULO_DO_TIPO) as TipoDeOrdem[])
                  .filter((t) => t !== 'preventiva')
                  .map((t) => ({ valor: t, rotulo: ROTULO_DO_TIPO[t] }))}
              />
            </Campo>
          </div>
        ) : null}
      </div>

      {/* Primário à direita, o de sair como terciário à esquerda (p.15, p.29). */}
      <div className="barra-de-acoes">
        <Botao
          nivel="terciario"
          type="button"
          onClick={() => {
            aoFechar(null);
          }}
        >
          Descartar O.S.
        </Botao>
        <Botao nivel="primario" type="submit" disabled={abrir.isPending}>
          {abrir.isPending ? 'Abrindo…' : 'Abrir O.S.'}
        </Botao>
      </div>
    </form>
  );
}

/** "Parada 06/10, 13:00 às 17:00", em violeta, com o `calendar-off` (R06, p.70). */
function LinhaDaParada({ de, ate }: { readonly de: Date; readonly ate: Date }) {
  const atrasada = momentoDaParada(de, ate) === 'atrasada';
  return (
    <span className={`txt-apoio flex items-center gap-1 whitespace-nowrap ${atrasada ? 'text-parada-texto' : 'text-deslocamento-texto'}`}>
      <CalendarOff aria-hidden="true" className="size-[var(--icone-controle)] shrink-0" />
      {atrasada ? linhaCurtaDaParada(de, ate) : `Parada ${janelaCurta(de, ate)}`}
    </span>
  );
}
